import { z } from 'zod';

/**
 * Contracten van Master (bundel MST) die bij een organisatie horen: archiveren en definitief wissen.
 *
 * Een eigen bestand naast `index.ts`, zoals `brand.ts`: het is een afgebakend deel, en zo botst het
 * niet met wie tegelijk aan de rest van `tenants` werkt.
 */

/** Archiveren: opgeschort en uit de gewone lijst. De reden blijft bij de organisatie. */
export const archiveTenantRequestSchema = z.object({
  reason: z.string().min(3).max(500),
});
export type ArchiveTenantRequest = z.infer<typeof archiveTenantRequestSchema>;

/**
 * Definitief wissen (ADR-0089). Alleen voor een gearchiveerde organisatie, en alleen als de code
 * letterlijk overgetypt is: een tweede, bewuste stap na het archiveren.
 */
export const purgeTenantRequestSchema = z.object({
  confirmCode: z.string().min(1).max(64),
});
export type PurgeTenantRequest = z.infer<typeof purgeTenantRequestSchema>;

export const purgeTenantResponseSchema = z.object({
  tenantId: z.guid(),
  code: z.string(),
  name: z.string(),
  /** Hoeveel rijen er in totaal gewist zijn, over alle modules. */
  rows: z.number().int(),
});
export type PurgeTenantResponse = z.infer<typeof purgeTenantResponseSchema>;

// --- MST-4: functies platformbreed -------------------------------------------------------------

/**
 * Eén functie zoals Master ze over alle organisaties heen ziet: de standaard uit de code, de
 * platformstandaard (als Master die zette), en hoeveel organisaties ze aan of uit hebben.
 */
export const platformFeatureSchema = z.object({
  key: z.string(),
  module: z.string(),
  description: z.string(),
  owner: z.string(),
  /** Uit de code of het omgevingsbestand: wat geldt zonder Master. */
  codeDefault: z.boolean(),
  /** Door Master gezet voor het hele platform; `null` = niet gezet, de code geldt. */
  platformDefault: z.boolean().nullable(),
  /** Wat een organisatie zonder eigen instelling krijgt. */
  defaultValue: z.boolean(),
  /** Hoeveel organisaties (niet gearchiveerd) de functie nu effectief aan hebben. */
  tenantsOn: z.number().int(),
  /** Hoeveel organisaties er een eigen instelling voor hebben. */
  tenantsOverridden: z.number().int(),
  tenantsTotal: z.number().int(),
});
export type PlatformFeature = z.infer<typeof platformFeatureSchema>;

export const platformFeatureListSchema = z.object({ items: z.array(platformFeatureSchema) });
export type PlatformFeatureList = z.infer<typeof platformFeatureListSchema>;

/** De platformstandaard zetten; `null` haalt ze weg, dan geldt de code weer. */
export const updatePlatformDefaultsRequestSchema = z.object({
  defaults: z.record(z.string(), z.boolean().nullable()),
});
export type UpdatePlatformDefaultsRequest = z.infer<typeof updatePlatformDefaultsRequestSchema>;

/** Per organisatie: de effectieve waarde van elke functie, en welke ze zelf ingesteld heeft. */
export const featureMatrixRowSchema = z.object({
  tenantId: z.guid(),
  name: z.string(),
  code: z.string(),
  status: z.string(),
  values: z.record(z.string(), z.boolean()),
  overridden: z.array(z.string()),
});
export type FeatureMatrixRow = z.infer<typeof featureMatrixRowSchema>;

export const featureMatrixSchema = z.object({
  flags: z.array(z.string()),
  rows: z.array(featureMatrixRowSchema),
});
export type FeatureMatrix = z.infer<typeof featureMatrixSchema>;

/**
 * Eén functie voor veel organisaties tegelijk. `tenantIds` leeg = elke organisatie die niet
 * gearchiveerd is. `enabled: null` haalt hun eigen instelling weg.
 */
export const bulkFeatureRequestSchema = z.object({
  flagKey: z.string().min(1),
  enabled: z.boolean().nullable(),
  tenantIds: z.array(z.guid()).max(1000).default([]),
});
export type BulkFeatureRequest = z.infer<typeof bulkFeatureRequestSchema>;

export const bulkFeatureResponseSchema = z.object({ changed: z.number().int() });
export type BulkFeatureResponse = z.infer<typeof bulkFeatureResponseSchema>;

// --- MST-6: pakketten en abonnementen ----------------------------------------------------------

/** Een pakket. Prijzen in eurocent; `maxUsers: null` = onbeperkt. */
export const planSchema = z.object({
  id: z.guid(),
  key: z.string(),
  name: z.string(),
  description: z.string(),
  pricePerMonthCents: z.number().int(),
  pricePerUserCents: z.number().int(),
  maxUsers: z.number().int().nullable(),
  /** De functies die in dit pakket zitten (sleutels van featureflags). */
  includedFlags: z.array(z.string()),
  active: z.boolean(),
  sortOrder: z.number().int(),
});
export type Plan = z.infer<typeof planSchema>;

export const planListSchema = z.object({ items: z.array(planSchema) });
export type PlanList = z.infer<typeof planListSchema>;

export const createPlanRequestSchema = z.object({
  key: z
    .string()
    .min(2)
    .max(32)
    .regex(/^[a-z0-9-]+$/, 'Gebruik kleine letters, cijfers en koppeltekens.'),
  name: z.string().min(2).max(60),
  description: z.string().max(400).default(''),
  pricePerMonthCents: z.number().int().min(0).max(100_000_000),
  pricePerUserCents: z.number().int().min(0).max(1_000_000).default(0),
  maxUsers: z.number().int().positive().nullable().default(null),
  includedFlags: z.array(z.string()).max(100).default([]),
  active: z.boolean().default(true),
  sortOrder: z.number().int().default(0),
});
export type CreatePlanRequest = z.infer<typeof createPlanRequestSchema>;

/** Wijzigen: alles behalve de sleutel. */
export const updatePlanRequestSchema = createPlanRequestSchema.omit({ key: true }).partial();
export type UpdatePlanRequest = z.infer<typeof updatePlanRequestSchema>;

export const subscriptionStatusSchema = z.enum(['trial', 'active', 'paused', 'cancelled']);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

const dateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik JJJJ-MM-DD.');

/** Het abonnement van één organisatie. */
export const subscriptionSchema = z.object({
  tenantId: z.guid(),
  planId: z.guid(),
  status: subscriptionStatusSchema,
  startedAt: dateOnly,
  trialEndsAt: dateOnly.nullable(),
  discountPct: z.number().int().min(0).max(100),
  billingEmail: z.string().nullable(),
  notes: z.string(),
  updatedAt: z.string().datetime(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

export const subscriptionListSchema = z.object({ items: z.array(subscriptionSchema) });
export type SubscriptionList = z.infer<typeof subscriptionListSchema>;

export const setSubscriptionRequestSchema = z.object({
  planId: z.guid(),
  status: subscriptionStatusSchema.default('active'),
  startedAt: dateOnly.optional(),
  trialEndsAt: dateOnly.nullable().default(null),
  discountPct: z.number().int().min(0).max(100).default(0),
  billingEmail: z.string().email().max(320).nullable().default(null),
  notes: z.string().max(2000).default(''),
});
export type SetSubscriptionRequest = z.infer<typeof setSubscriptionRequestSchema>;
